import { DestroyRef, inject, Injectable } from '@angular/core';
import { User } from '../../models/user.type';
import { BehaviorSubject, concatMap, filter, from, Observable, of, tap } from 'rxjs';
import Keycloak from 'keycloak-js';
import { Environment } from '../../../../environments/environment';
import { Account } from '../../models/account.type';
import { Organization } from '../../models/organization.type';
import { Assistant } from '../../models/assistant.type';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { extractJwtSub } from './jwt.utils';
import { SelectionStorageAdapter } from '../storage/selection-storage.adapter';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import { LoggerService } from '../../services/logger.service';

/**
 * Gerencia a segurança e a sessão do usuário autenticado: integra-se ao
 * Keycloak, mantém o usuário/contexto correntes (conta, organização,
 * assistente), expõe o token vigente e sua expiração, e coordena troca de
 * conta/organização/assistente, login mockado e logout.
 */
@Injectable({
    providedIn: 'root',
})
export class SecurityManager {
    private readonly logger = inject(LoggerService);
    private _userSub: string | null = null;

    private currentUser: User | null = null;
    private readonly currentUserSubject: BehaviorSubject<User | null> =
        new BehaviorSubject<User | null>(null);
    private readonly $currentUser = this.currentUserSubject.asObservable();

    constructor(
        private keycloak: Keycloak,
        private httpClient: HttpClient,
        private destroyRef: DestroyRef,
        private storageAdapter: SelectionStorageAdapter,
    ) {
        const keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
        toObservable(keycloakSignal)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(
                    ({ type }) =>
                        type === KeycloakEventType.AuthRefreshSuccess ||
                        type === KeycloakEventType.AuthRefreshError,
                ),
            )
            .subscribe(({ type }) => {
                if (type === KeycloakEventType.AuthRefreshSuccess) {
                    if (this.currentUser && this.keycloak.token) {
                        this.currentUser.token = this.keycloak.token;
                    }
                } else {
                    // Refresh falhou irrecuperavelmente → forçar logout
                    this.signOut();
                }
            });
    }

    /** Carrega as organizações do usuário e ajusta a organização corrente. */
    getUserOrganizations(): Observable<Organization[]> {
        return this.httpClient
            .get<Organization[]>(`${Environment.apiUrl}/me/organizations`)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap((organizations) => {
                    if (this.currentUser) {
                        this.currentUser.organizations = organizations;
                        this.handleCurrentOrganization();
                    }
                }),
            );
    }

    private handleCurrentOrganization(): void {
        if (!this.currentUser) return;
        const orgs = this.currentUser.organizations || [];
        const storedId = this.storageAdapter.getOrgId();
        if (storedId) {
            const found = orgs.find((o) => o.id === storedId);
            if (found) {
                this.currentUser.currentOrganization = found;
                this.currentUserSubject.next(this.currentUser);
                this.loadDefaultAssistantForCurrentOrganization();
                return;
            }
            // Organização armazenada não é mais válida — remove
            this.storageAdapter.clearOrg();
        }
        this.currentUser.currentOrganization = orgs.length === 1 ? orgs[0] : null;
        if (this.currentUser.currentOrganization) {
            this.storageAdapter.setOrgId(this.currentUser.currentOrganization.id);
        } else {
            this.storageAdapter.clearOrg();
        }
        this.currentUserSubject.next(this.currentUser);
        this.loadDefaultAssistantForCurrentOrganization();
    }

    /**
     * Carrega o assistente oculto da organização corrente (modo Fora das Contas)
     * a partir do `defaultAssistantId` e o anexa em `currentOrganization.defaultAssistant`.
     * Seguro para chamar múltiplas vezes — só faz fetch se ainda não estiver carregado.
     */
    private loadDefaultAssistantForCurrentOrganization(): void {
        if (!this.currentUser) return;
        const org = this.currentUser.currentOrganization;
        if (!org || !org.defaultAssistantId || org.defaultAssistant) return;

        this.httpClient
            .get<Assistant>(`${Environment.apiUrl}/assistants/${org.defaultAssistantId}`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (assistant) => {
                    if (!this.currentUser || !this.currentUser.currentOrganization) return;
                    this.currentUser.currentOrganization.defaultAssistant = assistant;
                    this.currentUserSubject.next(this.currentUser);
                },
                error: (error) => {
                    this.logger.warn(
                        'Falha ao carregar o assistente oculto da organização:',
                        error,
                    );
                },
            });
    }

    /** Carrega as contas do usuário e ajusta a conta corrente. */
    getUserAccounts(): Observable<Account[]> {
        return this.httpClient
            .get<Account[]>(`${Environment.apiUrl}/me/accounts`)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap((accounts) => {
                    if (this.currentUser) {
                        // Modelo organization-centered: backend retorna
                        // status 'active' (lowercase) para contas ativas.
                        this.currentUser.accounts = accounts.filter(
                            (account) => account.status === 'active',
                        );
                        this.handleCurrentAccount();
                    }
                }),
            );
    }

    private handleCurrentAccount(): void {
        if (!this.currentUser) return;
        const currentOrgId = this.currentUser.currentOrganization?.id;
        // Sem organização corrente não há como compor a chave hierárquica:
        // adia para quando o usuário escolher a organização.
        if (!currentOrgId) return;

        const storedId = this.storageAdapter.getAccountId(currentOrgId);
        if (storedId) {
            const found = this.currentUser.accounts.find(
                (a) => a.id === storedId && a.organizationId === currentOrgId,
            );
            if (found) {
                this.currentUser.currentAccount = found;
                this.currentUserSubject.next(this.currentUser);
                return;
            }
            // ID stored não é mais válido — remove
            this.storageAdapter.clearAccount(currentOrgId);
        }
        const defaultAccount = this.currentUser.accounts.find((a) => a.isDefault);
        this.currentUser.currentAccount = defaultAccount ?? null;
        if (this.currentUser.currentAccount) {
            // Paridade com handleCurrentOrganization: ao auto-selecionar,
            // persistimos o ID para que o próximo F5 não precise re-resolver
            // pelo fallback e para que `purgeOrphans` enxergue a chave.
            this.storageAdapter.setAccountId(currentOrgId, this.currentUser.currentAccount.id);
        }
        this.currentUserSubject.next(this.currentUser);
    }

    /**
     * Autentica o usuário a partir de um token e carrega seus dados.
     *
     * @param token Token de acesso.
     */
    authenticate(token: string): Observable<any> {
        return this.httpClient
            .post<Observable<any>>(`${Environment.apiUrl}/iam/authenticate`, { token })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap((data: any) => {
                    this._userSub = extractJwtSub(token);
                    this.storageAdapter.setUserId(this._userSub);
                    if (!this._userSub) {
                        // Sem `sub` válido não há como persistir seleções.
                        // Bootstrap continua para não bloquear a UX.
                        this.logger.warn(
                            'JWT sem `sub` válido — seleções não serão persistidas.',
                        );
                    } else {
                        this.storageAdapter.purgeOtherUsers();
                        this.purgeLegacyStorageKeys(this._userSub);
                    }
                    this.currentUser = {
                        id: '',
                        name: data.name,
                        email: data.email,
                        token: token,
                        photo: data.photo,
                        isAdministrator: data.is_administrator,
                        accounts: [],
                        currentAccount: null,
                        organizations: [],
                        currentOrganization: null,
                        hasAccountAssistants: false,
                        currentAssistant: null,
                    } as unknown as User;
                }),
                concatMap(() => {
                    if (Environment.useMockedLogin) {
                        return from(Promise.resolve(Environment.mockedUser));
                    } else {
                        return from(this.keycloak.loadUserInfo());
                    }
                }),
                tap((userInfo: any) => {
                    this.currentUser!.photo = userInfo['FOTO'] || null;
                    this.currentUserSubject.next(this.currentUser);
                }),
            );
    }

    /**
     * Limpeza one-shot das chaves dos formatos antigos (pré-refator
     * sessionStorage hierárquico). Idempotente: rodadas seguintes não
     * encontram nada a remover.
     *
     * Escopo: apenas chaves do usuário corrente. Chaves de outros usuários
     * (caso o mesmo dispositivo seja compartilhado) permanecem até a
     * própria sessão deles rodar este código.
     */
    private purgeLegacyStorageKeys(userSub: string): void {
        try {
            // sessionStorage — JSON antigo de conta sem prefixo de usuário.
            sessionStorage.removeItem('currentAccount');
        } catch {
            // sessionStorage indisponível — ignorar.
        }
        try {
            const orgPrefix = `${userSub}:currentOrganization`;
            const assistantPrefix = `${userSub}:currentAssistant_`;
            const keys = Object.keys(localStorage);
            for (const key of keys) {
                if (key === orgPrefix || key.startsWith(assistantPrefix)) {
                    try {
                        localStorage.removeItem(key);
                    } catch {
                        // ignora
                    }
                }
            }
        } catch {
            // localStorage indisponível — ignorar.
        }
    }

    /**
     * Consulta o endpoint `GET /iam/me` (backend) para obter o perfil
     * sintético do usuário autenticado (`email`, `is_administrator`,
     * `organization_id`, `organization_name`, `account_id`).
     *
     * O endpoint faz parte da allowlist do guard de subscrição inativa,
     * portanto é consumível mesmo quando a organização está bloqueada
     * por HTTP 402 — útil para confirmar identidade na tela de
     * "subscrição inativa".
     */
    me(): Observable<{
        email: string;
        is_administrator: boolean;
        organization_id: string | null;
        organization_name: string | null;
        account_id: string | null;
    }> {
        return this.httpClient
            .get<{
                email: string;
                is_administrator: boolean;
                organization_id: string | null;
                organization_name: string | null;
                account_id: string | null;
            }>(`${Environment.apiUrl}/iam/me`)
            .pipe(takeUntilDestroyed(this.destroyRef));
    }

    /** Stream que emite o usuário corrente a cada mudança (ou `null` no logout). */
    onCurrentUserChanged(): Observable<User | null> {
        return this.$currentUser;
    }

    /** Retorna o usuário autenticado corrente (ou `null`). */
    getCurrentUser(): User | null {
        return this.currentUser;
    }

    /**
     * Retorna o token JWT mais recente disponível.
     *
     * Em produção, lê diretamente de `keycloak.token`, que é atualizado
     * pelo `AutoRefreshTokenService` a cada renovação silenciosa — evitando
     * que o `SecurityInterceptor` envie um token expirado.
     *
     * Em ambiente mockado, `keycloak.token` é sempre `undefined` (o
     * `MockedKeycloakAdapter` não executa o fluxo real de auth), por isso
     * recai no valor estático armazenado em `currentUser.token`.
     */
    getLiveToken(): string | null {
        if (Environment.useMockedLogin) {
            return this.currentUser?.token ?? null;
        }
        return this.keycloak.token ?? null;
    }

    /**
     * Retorna o instante de expiração do token (epoch em segundos), ou
     * `null` quando não há token real com expiração (ex.: ambiente mockado).
     */
    getTokenExpiration(): number | null {
        if (Environment.useMockedLogin) {
            return null;
        }
        return this.keycloak.tokenParsed?.exp ?? null;
    }

    /**
     * Retorna quantos segundos faltam para o token expirar, ou `null` quando
     * não há expiração conhecida (ex.: ambiente mockado).
     */
    getSecondsUntilExpiry(): number | null {
        const exp = this.getTokenExpiration();
        if (exp === null) {
            return null;
        }
        return exp - Math.floor(Date.now() / 1000);
    }

    /**
     * Renova a sessão junto ao Keycloak.
     *
     * @param minValidity Validade mínima (em segundos) que força a renovação.
     * @returns Promise que resolve `true` se o token foi efetivamente renovado.
     */
    refreshSession(minValidity: number = 300): Promise<boolean> {
        return this.keycloak.updateToken(minValidity);
    }

    /** Retorna a conta corrente (ou `null`). */
    getCurrentAccount(): Account | null {
        return this.currentUser?.currentAccount ?? null;
    }

    /** Retorna a organização corrente (ou `null`). */
    getCurrentOrganization(): Organization | null {
        return this.currentUser?.currentOrganization ?? null;
    }

    /** Indica se o usuário pertence a pelo menos uma organização. */
    hasOrganization(): boolean {
        return (this.currentUser?.organizations?.length ?? 0) > 0;
    }

    /** Encerra a sessão: limpa seleções persistidas e faz logout no Keycloak. */
    signOut(): void {
        // Limpa chaves do adapter antes do redirect — defensivo. Se
        // keycloak.logout falhar parcialmente ou logoutRedirectUri for
        // alterado no futuro, o invariante "sem sessão = sem seleção
        // persistida" continua válido.
        this.storageAdapter.clearAllForCurrentUser();
        this.keycloak
            .logout({ redirectUri: Environment.logoutRedirectUri })
            .then(() => {
                this.currentUserSubject.next(null);
            });
    }

    /**
     * Troca a conta corrente.
     *
     * @param accountId Id da conta para a qual trocar.
     * @param reload Se deve recarregar a aplicação após a troca.
     */
    switchAccount(accountId: string, reload: boolean = true): void {
        if (this.currentUser) {
            this.currentUser.currentAccount = this.currentUser.accounts.find(
                (account) => account.id === accountId,
            )!;
            const orgId = this.currentUser.currentOrganization?.id;
            if (orgId && this.currentUser.currentAccount) {
                this.storageAdapter.setAccountId(
                    orgId,
                    this.currentUser.currentAccount.id,
                );
            }
            this.currentUserSubject.next(this.currentUser);
            if (reload) window.location.reload();
        }
    }

    /** Troca para a conta padrão ("Minha Conta") do usuário. */
    switchToDefaultAccount(): void {
        if (!this.currentUser) return;
        const defaultAccount = this.currentUser.accounts.find((a) => a.isDefault);
        if (defaultAccount) {
            this.currentUser.currentAssistant = null;
            this.currentUser.hasAccountAssistants = false;
            this.switchAccount(defaultAccount.id, false);
        }
    }

    /**
     * Limpa a organização e a conta correntes (sem deslogar). Usado
     * pela tela de "subscrição inativa" quando o usuário pede para
     * trocar de organização — habilita o `selectOrganizationGuard`
     * a permitir acesso a `/select-organization`.
     */
    clearCurrentOrganization(): void {
        if (!this.currentUser) return;
        // Captura IDs antes de zerar in-memory — necessário para limpar
        // as chaves de conta e assistente que dependem deles.
        const prevOrgId = this.currentUser.currentOrganization?.id ?? null;
        const prevAccountId = this.currentUser.currentAccount?.id ?? null;

        this.currentUser.currentAssistant = null;
        this.currentUser.hasAccountAssistants = false;
        this.currentUser.currentOrganization = null;
        this.currentUser.currentAccount = null;

        if (prevOrgId && prevAccountId) {
            this.storageAdapter.clearAssistant(prevOrgId, prevAccountId);
        }
        if (prevOrgId) {
            this.storageAdapter.clearAccount(prevOrgId);
        }
        this.storageAdapter.clearOrg();
        this.currentUserSubject.next(this.currentUser);
    }

    /**
     * Troca a organização corrente.
     *
     * @param orgId Id da organização para a qual trocar.
     */
    switchOrganization(orgId: string): void {
        if (!this.currentUser) return;
        const org = this.currentUser.organizations.find((o) => o.id === orgId);
        if (org) {
            this.currentUser.currentAssistant = null;
            this.currentUser.hasAccountAssistants = false;
            this.currentUser.currentOrganization = org;
            this.currentUser.currentAccount = null;
            this.currentUser.accounts = [];
            this.storageAdapter.setOrgId(org.id);
            // A chave de conta da org anterior fica órfã na sessão —
            // será coberta por `purgeOrphans` no próximo bootstrap.
            this.getUserAccounts().subscribe({
                next: () => this.loadDefaultAssistantForCurrentOrganization(),
                error: (err) =>
                    this.logger.error(
                        '[SecurityManager] falha ao carregar contas em switchOrganization',
                        err,
                    ),
            });
        }
    }

    /**
     * Define o assistente corrente a partir da lista disponível (ex.: após
     * carregar os assistentes da conta/organização).
     *
     * @param assistants Assistentes disponíveis.
     */
    handleCurrentAssistant(assistants: Assistant[]): void {
        const account = this.currentUser?.currentAccount;
        const orgId = this.currentUser?.currentOrganization?.id;
        if (!this.currentUser || !account || !orgId) {
            if (this.currentUser) {
                this.currentUser.hasAccountAssistants = false;
                this.currentUser.currentAssistant = null;
                this.currentUserSubject.next(this.currentUser);
            }
            return;
        }

        const accountAssistants = assistants.filter((a) => !a.isOpen);
        this.currentUser.hasAccountAssistants = accountAssistants.length > 0;

        if (!this.currentUser.hasAccountAssistants) {
            this.currentUser.currentAssistant = null;
            this.currentUserSubject.next(this.currentUser);
            return;
        }

        const storedId = this.storageAdapter.getAssistantId(orgId, account.id);

        if (storedId) {
            const found = accountAssistants.find((a) => a.id === storedId);
            if (found) {
                this.currentUser.currentAssistant = found;
                this.currentUserSubject.next(this.currentUser);
                return;
            }
            this.storageAdapter.clearAssistant(orgId, account.id);
        }

        if (accountAssistants.length === 1) {
            this.currentUser.currentAssistant = accountAssistants[0];
            this.storageAdapter.setAssistantId(orgId, account.id, accountAssistants[0].id!);
            this.currentUserSubject.next(this.currentUser);
            return;
        }

        this.currentUser.currentAssistant = null;
        this.currentUserSubject.next(this.currentUser);
    }

    /**
     * Troca o assistente corrente.
     *
     * @param assistant Assistente para o qual trocar.
     */
    switchAssistant(assistant: Assistant): void {
        const accountId = this.currentUser?.currentAccount?.id;
        const orgId = this.currentUser?.currentOrganization?.id;
        if (!this.currentUser || !accountId || !orgId) return;

        this.currentUser.currentAssistant = assistant;
        this.storageAdapter.setAssistantId(orgId, accountId, assistant.id!);
        this.currentUserSubject.next(this.currentUser);
    }

    /** Indica se a conta corrente possui assistentes. */
    hasAccountAssistants(): boolean {
        return this.currentUser?.hasAccountAssistants ?? false;
    }

    /** Indica se há algum assistente aberto disponível. */
    hasOpenAssistant(): boolean {
        return !!this.currentUser?.currentAccount?.openAssistant;
    }

    /** Indica se o usuário pode conversar (há assistente utilizável). */
    canChat(): boolean {
        if (this.currentUser?.currentAccount) {
            return this.hasAccountAssistants() || this.hasOpenAssistant();
        }
        return !!this.currentUser?.currentOrganization?.defaultAssistant;
    }

    /** Retorna o assistente corrente (ou `null`). */
    getCurrentAssistant(): Assistant | null {
        return this.currentUser?.currentAssistant ?? null;
    }
}
